import React from 'react'
import { Button, ButtonProps, styled } from '@mui/material'
import { PaletteColor } from '@mui/material/styles'

import '~components/styles/global.less'

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    link: true
  }
}

interface StyledButtonProps extends ButtonProps {
  boxShadow?: boolean
  size?: 'small' | 'medium' | 'large'
  variant?: 'contained' | 'outlined' | 'text' | 'link'
}

const StyledButton = styled(Button)<StyledButtonProps>(({
  theme,
  boxShadow,
  color = 'secondary',
  variant,
  size,
}) => {
  const getColor = (): string => {
    if (variant === 'contained') {
      return (theme.palette[color as keyof typeof theme.palette] as PaletteColor)?.contrastText
    }
    return (theme.palette[color as keyof typeof theme.palette] as PaletteColor)?.main
  }

  const buttonColor = getColor() || theme.palette.text.primary

  if (variant === 'link') {
    return {
      padding: 0,
      minWidth: 0,
      backgroundColor: 'transparent',
      color: buttonColor,
      '&:hover': {
        backgroundColor: 'transparent',
        opacity: 0.8,
      },
      '&:active': {
        backgroundColor: 'transparent',
      },
    }
  }

  const baseStyles = {
    display: 'inline-flex',
    maxWidth: size === 'small' ? '20%' : size === 'large' ? '100%' : '50%',
    padding: '7px 24px',
    gap: '10px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    borderRadius: '4px',
    boxShadow: boxShadow
      ? 'rgba(60, 64, 67, 0.08) 0px 1px 3px 0px, rgba(130, 130, 130, 0.15) 0px 1px 3px 3px'
      : 'none',
    transition: 'all 0.2s ease-in-out',
    color: buttonColor,
  }

  if (variant === 'outlined') {
    return {
      ...baseStyles,
      border: `1px solid ${buttonColor}`,
      backgroundColor: 'transparent',
      '&.MuiButton-outlined': {
        border: `1px solid ${buttonColor}`,
      },
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        opacity: 0.8,
        border: `1px solid ${buttonColor}`,
      },
      '&:active': {
        boxShadow: 'none',
        transform: 'scale(0.95)',
        border: `1px solid ${buttonColor}`,
        backgroundColor: 'transparent',
      },
    }
  }

  return {
    ...baseStyles,
    backgroundColor:
      variant === 'contained'
        ? (theme.palette[color as keyof typeof theme.palette] as PaletteColor)?.main
        : 'transparent',
    '&:hover': {
      backgroundColor:
        variant === 'contained'
          ? (theme.palette[color as keyof typeof theme.palette] as PaletteColor)?.main
          : theme.palette.action.hover,
      opacity: 0.8,
      transition: 'opacity 0.2s ease-in-out',
    },
    '&:active': {
      boxShadow: 'none',
      transform: 'scale(0.95)',
      color: buttonColor,
      backgroundColor:
        variant === 'contained'
          ? (theme.palette[color as keyof typeof theme.palette] as PaletteColor)?.main
          : 'transparent',
    },
  }
})

const CustomButton: React.FC<StyledButtonProps> = ({ boxShadow, children, variant, ...props }) => {
  return (
    <StyledButton
      boxShadow={boxShadow}
      variant={variant}
      {...props}
      className='double-blue-border-on-focus'
    >
      {children}
    </StyledButton>
  )
}

export default CustomButton
